import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getTotalItems, getTotalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="cart-page-empty">
        <Navbar />
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-12"
            >
              <ShoppingCart className="w-24 h-24 text-slate-300 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Coșul tău este gol
              </h1>
              <p className="text-slate-600 mb-8">
                Nu ai adăugat încă niciun produs în coș.
              </p>
              <Link to="/magazin">
                <Button className="btn-primary" data-testid="go-to-shop-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Mergi la Magazin
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="cart-page">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="cart-title">
              Coșul Meu
            </h1>
            <p className="text-slate-600">{getTotalItems()} produse în coș</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-4 flex items-center gap-4"
                  data-testid={`cart-item-${item.id}`}
                >
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {item.name}
                    </h3>
                    <p className="text-sm text-slate-600">{item.category}</p>
                    <p className="text-orange-500 font-bold mt-1">
                      {item.price.toFixed(2)} LEI / {item.unit}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      data-testid={`decrease-${item.id}`}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 0)}
                      className="w-16 text-center"
                      min="0"
                      data-testid={`quantity-input-${item.id}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="btn-primary"
                      data-testid={`increase-${item.id}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="text-right min-w-[100px]">
                    <p className="font-bold text-lg text-slate-900">
                      {(item.price * item.quantity).toFixed(2)} LEI
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-${item.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="glass-card p-6 sticky top-24"
                data-testid="order-summary"
              >
                <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Sumar Comandă
                </h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal ({getTotalItems()} produse)</span>
                    <span>{getTotalPrice().toFixed(2)} LEI</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Transport</span>
                    <span className="text-green-600">Se va calcula</span>
                  </div>
                  <div className="border-t border-slate-200 pt-3">
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total</span>
                      <span className="text-orange-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {getTotalPrice().toFixed(2)} LEI
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Prețurile includ TVA</p>
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/checkout')}
                  className="btn-primary w-full mb-3"
                  data-testid="go-to-checkout-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  MERGI SPRE METODA DE PLATĂ
                </Button>
                
                <Link to="/magazin">
                  <Button variant="outline" className="w-full" data-testid="continue-shopping-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Continuă Cumpărăturile
                  </Button>
                </Link>

                <button
                  onClick={clearCart}
                  className="w-full mt-4 text-sm text-red-500 hover:text-red-700 transition"
                  data-testid="clear-cart-btn"
                >
                  Golește coșul
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
